/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import * as color from '@constant/color';
import { v4 as uuid4 } from 'uuid';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { shuffleIndicesWithSeed } from '@util/random';

interface IProps {
  workbookKey: string;
  choice: string[];
  choicePronunciation: string[];
  answer: string;
  isAnswerView: boolean;
  selectedChoice: string | null;
  handleChoiceSelect: (choice: string) => void;
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
`;

const ChoiceContainer = styled.div<{
  $design: { backgroundColor: string; borderColor: string; click: boolean };
}>`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  padding: 5px 5px 5px 5px;
  // box-shadow: ${color.SURFACE_SHADOW};
  border-radius: ${size.BORDER_RADIUS_1};
  border: ${size.BORDER_WIDTH_1} solid
    ${(props) => (props.$design.borderColor === 'transparent' ? color.GRAY_4 : props.$design.borderColor)};
  background-color: ${(props) => props.$design.backgroundColor || 'transparent'};
  cursor: ${(props) => (props.$design.click ? 'default' : 'pointer')};

  ${(props) =>
    !props.$design.click &&
    !IS_IYAGIGO_MOBILE_VIEW &&
    `
    &:hover {
      box-shadow: none;
      border-color: ${color.GRAY_2};
      // transform: translateY(-1px);
    }
  `}
`;

const CHOICE_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left', event: { cursor: 'pointer' } },
} as const;

const VocabularyChoice: React.FC<IProps> = ({
  workbookKey,
  choice,
  choicePronunciation,
  answer,
  isAnswerView,
  selectedChoice,
  handleChoiceSelect,
}) => {
  // ############### shuffle ###############
  // workbookKey를 시드로 choice/choicePronunciation을 동일 permutation으로 결정론적 셔플
  const shuffledIndices = useMemo(
    () => shuffleIndicesWithSeed(choice.length, workbookKey),
    [choice.length, workbookKey],
  );
  const shuffledChoice = useMemo(() => shuffledIndices.map((i) => choice[i]), [choice, shuffledIndices]);
  const shuffledPronunciation = useMemo(
    () => shuffledIndices.map((i) => choicePronunciation?.[i]),
    [choicePronunciation, shuffledIndices],
  );

  // ############### function ###############
  const createBorderDesign = (isAnswerView: boolean, isAnswer: boolean, isSelected: boolean): string => {
    if (isAnswerView) {
      if (isAnswer) {
        return color.GREEN_4;
      } else if (isSelected) {
        return color.RED_4;
      }
    } else {
      if (isSelected) {
        return color.YELLOW_3;
      }
    }
    return 'transparent';
  };

  // ############### handler ###############
  const handleChoiceClick = (answer: string) => {
    if (isAnswerView) return;
    handleChoiceSelect(answer);
  };

  // ############### items ###############
  const choices = shuffledChoice.map((choice, idx) => {
    return {
      id: idx + 1,
      text: choice,
      // 정답의 '문자열'과 보기의 '문자열'이 같으면 '정답' 체크
      isAnswer: answer === choice,
    };
  });

  const Choices = choices.map((item) => {
    const isSelected = selectedChoice === item.text;
    const pronunciation = shuffledPronunciation?.[item.id - 1];

    return (
      <ChoiceContainer
        key={uuid4()}
        onClick={() => handleChoiceClick(item.text)}
        $design={{
          borderColor: createBorderDesign(isAnswerView, item.isAnswer, isSelected),
          backgroundColor: 'transparent',
          click: isAnswerView,
        }}>
        <TextBlock
          text={item.text}
          annotation={pronunciation ? { reading: pronunciation } : undefined}
          design={CHOICE_TEXT_DESIGN}
        />
      </ChoiceContainer>
    );
  });

  return <Wrapper>{Choices}</Wrapper>;
};

export default VocabularyChoice;
